<template>
  <div
    class="home-page"
    data-test="page-home">
    <section
      class="hero"
      data-test="section-banner">
      <div class="banner">
        <div class="banner-background">
          <div class="container">
            <art-grid />
          </div>
        </div>
        <div class="banner-content container">
          <h1 class="banner-title">
            <span class="next-line">Original podcasts </span>
            <span class="next-line">with original ideas for </span>
            <span class="next-line">original people</span>
          </h1>
          <p class="banner-desc">Luminary is a premium podcast app that offers access to shows from top creators you won’t find anywhere else, plus the ones you already love.</p>
          <router-link
            to="/signup"
            class="el-button el-button--primary banner-btn"
            data-test="link-premiumfree">Try
            <span
              v-if="promoMeta.trialPeriod > 1"
              class="line-through">1 month</span> {{ promoMeta.formattedTrialPeriod }} of Premium free</router-link>
          <p class="fine-print">{{ localPricing.price | amountFilter(localPricing.currency) }} {{ localPricing.price | taxFilter(localPricing.currency) }} <br>
            <span class="fine-print dark">Cancel anytime.
              <router-link
                class="dark"
                target="_blank"
                to="/termsfreetrial"
                data-test="link-termfreetrial"
              >
                Terms apply.
              </router-link>
            </span>
          </p>
        </div>
      </div>
    </section>
    <section
      class="perks"
      data-test="section-perks">
      <div class="premium-perks container">
        <h4 class="section-title">WHAT WE OFFER</h4>
        <div class="premium-perks-content-container">
          <div
            v-for="(item, index) in premium_content"
            :key="`perk-item-${index}`"
            class="premium-perks-content">
            <h4>{{ item.title }}</h4>
            <p> {{ item.description }} </p>
          </div>
        </div>
      </div>
    </section>
    <section
      class="pricing-cards container"
      data-test="section-pricecard">
      <price-cards
        :is-yellow="true"
        :adjust-to-sm="true"/>
    </section>
    <section
      class="listen"
      data-test="section-app">
      <div class="app-download container">
        <div class="app-image">
          <img
            :v-lazy="srcSet('src', threePhonesImgs)"
            :srcset="srcSet('srcset', threePhonesImgs)">
        </div>
        <div class="app-download-info">
          <h4 class="section-title">HOW TO LISTEN</h4>
          <h2 class="title">Download the free app</h2>
          <ul>
            <li>Listen to the shows you love</li>
            <li>Stream, download and bookmark episodes you want to save for later</li>
            <li>Search by episode, podcast or publisher</li>
            <li>Upgrade to premium and access Luminary originals</li>
          </ul>
          <appstore-links />
        </div>
      </div>
    </section>
    <section
      class="premium-shows-section"
      data-test="section-shows">
      <div class="premium-shows container">
        <h4 class="section-title">PREMIUM SHOWS</h4>
        <h1 class="subtitle">Podcasts only on Luminary</h1>
        <loader
          v-show="!exclusive_podcasts_loaded"
          :with-margin="true"
          label="Fetching Luminary Exclusives..." />
        <podcast-list
          v-if="exclusive_podcasts_loaded"
          :bucket="exclusive_podcasts"
          type="podcast"/>
      </div>
    </section>
    <section
      class="cta"
      data-test="section-cta">
      <router-link
        to="/signup"
        class="el-button el-button--primary banner-btn"
        data-test="btn-monthfree">Try
        <span
          v-if="promoMeta.trialPeriod > 1"
          class="line-through">1 month</span> {{ promoMeta.formattedTrialPeriod }} free</router-link>
    </section>
  </div>
</template>

<script>
import supportsWebP from 'supports-webp';
import * as log from 'loglevel';
import PriceCards from '../components/PriceCards';
import Loader from '../components/Loader';
import PodcastService from '../services/Podcast';
import PodcastList from '../components/PodcastList';
import ArtGrid from '../components/ArtGrid';
import AppStoreLinks from '../components/AppStoreLinks';
import threePhones1x from '../assets/images/threePhones_1x.png';
import threePhones2x from '../assets/images/threePhones_2x.png';
import threePhones3x from '../assets/images/threePhones_3x.png';
import threePhones1xwebp from '../assets/images/threePhones_1x.png.webp';
import threePhones2xwebp from '../assets/images/threePhones_2x.png.webp';
import threePhones3xwebp from '../assets/images/threePhones_3x.png.webp';
import PageMixin from '../shared/pageMixin';

export default {
  name: 'Home',
  components: {
    'podcast-list': PodcastList,
    'price-cards': PriceCards,
    'art-grid': ArtGrid,
    'appstore-links': AppStoreLinks,
    Loader,
  },
  mixins: [PageMixin],
  data() {
    return {
      pageName: 'Landing',
      premium_content: [
        { title: 'A new free podcast app', description: 'Listen to your regular podcasts and utilize advanced search and personalized recommendations.' },
        { title: 'Luminary Premium', description: ' Get access to the 40+ original ad-free shows we’re launching this year on Luminary.' },
        { title: 'Customized for you', description: 'Customized recommendations tailored to your tastes and listening habits, helping you solve what to listen to next.' },
      ],
      podcast_service: new PodcastService(),
      exclusive_podcasts: {
        uuid: null,
        name: null,
        bucket_type: 'podcasts',
        media_size: 'thumbnail',
        items: [],
      },
      exclusive_podcasts_loaded: false,
      defaultPrice: this.$store.getters.pricing,
      threePhonesImgs: [],
      scrollEventListener: null,
    };
  },
  mounted() {
    const ctx = this;
    this.scrollEventListener = function () {
      ctx.getLuminaryExclusiveShows();
      document.body.onscroll = null;
    };
    document.body.onscroll = this.scrollEventListener;

    supportsWebP.then((supported) => {
      if (!supported) {
        this.threePhonesImgs = [threePhones1x, threePhones2x, threePhones3x];
      } else {
        this.threePhonesImgs = [threePhones1xwebp, threePhones2xwebp, threePhones3xwebp];
      }
    });
  },
  methods: {
    getLuminaryExclusiveShows() {
      this.podcast_service.getExclusivePodcasts().then((res) => {
        this.exclusive_podcasts.items = res.podcasts || [];
        this.exclusive_podcasts.items = res.podcasts.map(item => ({ ...item, publisher_name: item.description }));
        this.exclusive_podcasts.items_count = res.podcasts.length;
        this.exclusive_podcasts_loaded = true;
      }).catch(error => log.info(error.message));
    },
  },
};
</script>
<style lang="scss">
@import "../assets/scss/mixins.scss";
.home-page {
  .premium-shows-section {
    .premium-shows {
      .container {
        @media (max-width: 576px) {
          margin: 0px;
          width: 100%;
        }
        .podcast-list {
          .podcast-card {
            height: 355px;
            @media (max-width: 576px) {
              height: 250px;
            }
            .podcast-content {
              text-align: left;
              padding-top: 14px;
              .podcast-title {
                text-align: left;
                font-size: 18px;
                padding-bottom: 1px;
                display: inline;
                white-space: unset;
                line-height: 24px;
                @media (max-width: 576px) {
                  font-size: 14px;
                  line-height: 18px;
                }
              }
              .publisher-name {
                text-align: left;
                margin-top: 6px !important;
                font-size: 16px;
                letter-spacing: -0.2px;
                text-transform: none;
                @include multilinetruncate(16px, 22px, 2);
                @media (max-width: 576px) {
                  margin-top: 4px;
                  font-size: 12px;
                  line-height: 16px;
                  @include multilinetruncate(12px, 16px, 2);
                }
              }
            }
            &:hover {
              .podcast-content {
                .podcast-title {
                  border-bottom: 2px solid #00BEDE;
                }
              }
            }
          }
        }
      }
    }
  }
}
</style>

<style lang="scss" scoped>
@import "../assets/scss/variables.scss";

.home-page {
  .banner {
    width: 100%;
    /* max-height: 550px; */
    background-color: $black1;
    display: flex;
    overflow: hidden;
    position: relative;
    .banner-background {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
    }
    .banner-content {
      padding: 61px 50px 50px 50px;
      z-index: 1;
      .banner-title {
        font-size: 58px;
        letter-spacing: -1.53px;
        line-height: 62px;
        /* font-size: 76px;
        line-height: 79px;
        letter-spacing: -2px; */
        margin: 0 0 30px 0;
        max-width: 640px;
        font-family: $clearface-bold, Arial, sans-serif;
        color: #fff;
        .next-line {
          display: block;
          white-space: nowrap;
        }
      }
      .banner-desc {
        color: #fff;
        font-size: 20px;
        line-height: 28px;
        max-width: 491px;
        margin: 0 0;
        margin-bottom: 50px;
        font-family: Favorit-light, Arial, sans-serif;
        a {
          color: #fff;
          text-decoration: underline;
        }
      }
      .banner-btn {
        padding: 16px 23px;
        font-size: 17px;
        color: $black;
        background: $brand;
        border: none;
        text-decoration: underline;
        text-transform: none;
        margin-bottom: 22px;
        box-shadow: 6px 5px 0px rgba(255,229,0,0.3);
        &:hover {
          background-color: #C7B302 !important;
        }
      }
      .fine-print {
        color: $white;
        font-family: Favorit-light, Arial, sans-serif;
        font-size: 16px;
        letter-spacing: -0.21px;
        line-height: 22px;
        margin: 0px;
        & .dark {
          color: #9B9B9B;
        }
      }
      .fine-print > a {
        text-decoration: underline;
      }
    }
    .bg-image {
      max-width: 45%;
      max-height: 580px;
    }
  }

  .perks {
    background-color: #F5F4F4;
  }
  .premium-perks {
    padding: 51px 0 50px;
    text-align: center;
    .section-title {
      color: $black1;
      margin-top: 0px;
      font-weight: 600;
      font-size: 18px;
      letter-spacing: 0.9px;
    }
    .premium-perks-content-container {
      display: flex;
      .premium-perks-content {
        text-align: center;
        width: 33%;
        padding: 0 20px;
        &:not(:last-child) {
          border-right: 1px solid rgba(17,17,17,0.2);
        }
        h4 {
          font-family: "ITC Clearface" !important;
          font-size: 30px;
          line-height: 34px;
          color: $black;
          margin: 12px 0 !important;
          white-space: nowrap;
        }
        p {
          line-height: 24px;
          font-size: 18px;
          font-family: Favorit-light, Arial, sans-serif;
        }
      }
    }
  }
  .pricing-cards {
    padding: 70px 0 76px 0;
  }
  .listen {
    background-color: #F5F4F4;
  }
  .app-download {
    padding: 60px 0px 50px;
    display: flex;
    justify-content: space-between;
    overflow: hidden;
    .app-download-info {
      flex-grow: 1;
      padding-left: 45px;
      .section-title {
        font-size: 18px;
        font-weight: 600;
        color: #2B303A;
        margin-top: 23px;
        margin-bottom: 25px;
        letter-spacing: 1px;
      }
      .title {
        font-family: $clearface-bold, Arial, sans-serif;
        font-size: 50px;
        margin: 0 0 31px;
        font-weight: 400;
        letter-spacing: -0.52px;
      }
      .desc {
        line-height: 22px;
        letter-spacing: -0.2px;
        color: $black;
        max-width: 400px;
        height: 45px;
        text-overflow: ellipsis;
        font-weight: Favorit-light;
      }
      .mobileapp {
        display: none;
      }
      .appstore-images {
        margin: 34px 0 30px 0;
        svg {
          width: 160px;
          height: 48px;
          &:first-child {
            margin-right: 16px;
          }
        }
      }
      ul {
        font-size: 20px;
        font-family: Favorit-light, Arial, sans-serif;
        list-style: none;
        padding-left: 24px;
        margin-bottom: 49px;
        max-width: 385px;
        margin-left: -5px;
        li {
          margin-bottom: 16px;
          &::before {
            content: "•";
            color: $brand;
            display: inline-block;
            width: 1em;
            margin-left: -1em;
          }
        }
      }
    }
    .app-image {
      flex-grow: 1;
      padding-right: 45px;
      display: flex;
      flex-direction: column;
      justify-content: center;
      img {
        float: right;
        width: 90%;
        max-width: 532px;
        margin-left: auto;
        object-fit: contain;
      }
    }
  }
  .premium-shows {
    text-align: center;
    padding: 46px 0px 0px 0px;
    margin-bottom: 42px;
    width: 100%;
    .section-title {
      margin-top: 0px;
      margin-bottom: 0;
      font-size: 18px;
      font-weight: 600;
      letter-spacing: 1px;
      color: #2B303A;
    }
    .subtitle {
      margin: 16px 0 48px 0;
      line-height: 48px;
      font-size: 48px;
      font-family: $clearface-bold, Arial, sans-serif;
      letter-spacing: -2px;
    }
    .desc {
      font-size: 20px;
      line-height: 24px;
      color: #474747;
      margin: 22px 0 40px 0;
      font-family: Favorit-light, Arial, sans-serif;
    }
  }
  section.cta {
    display: flex;
    justify-content: center;
    margin-bottom: 80px;
    a {
      box-shadow: 4px 4px 0px 0px rgba(0, 190, 222, 0.4);
    }
  }
}

@media (max-width: 1200px) {
  .home-page {
    .banner {
      .banner-content {
        padding-left: 0px;
        .banner-title {
          font-size: 50px;
          line-height: 57px;
          max-width: 440px;
        }
        .banner-desc {
          max-width: 45%;
        }
      }
    }
  }
}
@media (max-width: 1024px) {
  .home-page {
    .banner {
      .banner-content {
        padding: 33px 0px 28px 32px;
        margin: 10px 409px 0 0px;
        .banner-title {
          font-size: 50px;
          line-height: 57px;
          letter-spacing: -1px;
          margin-bottom: 20px;
          /* .next-line {
            display: inline;
          } */
        }
        .banner-desc {
          font-size: 15px;
          line-height: 20px;
          margin-bottom: 28px;
          max-width: 68%;
        }
        .banner-btn {
          margin-bottom: 12px;
        }
        .fine-print {
          color: white;
          font-size: 14px;
          font-family: Favorit-light, Arial, sans-serif;
          &.dark {
            font-size: 14px;
            font-family: Favorit-light, Arial, sans-serif;
          }
        }
      }
    }
    .premium-perks {
      display: none;
    }
    .app-download {
      .app-download-info {
        .title {
          font-size: 40px;
          letter-spacing: -0.8px;
        }
        .desc {
          line-height: 22px;
          letter-spacing: -0.2px;
          color: $black;
          max-width: 400px;
          height: 45px;
          text-overflow: ellipsis;
          font-weight: Favorit-light;
        }
        .appstore-images {
          margin: 34px 0 30px 0;
          img {
            &:first-child {
              margin-right: 16px;
            }
          }
        }
      }
    }
  }
}

@media (max-width: 900px) {
  .home-page {
    .banner {
      .banner-content {
        .banner-title {
          font-size: 40px;
          line-height: 50px;
        }
      }
    }
  }
}

@media (max-width: 768px) {
  .home-page {
    .banner {
      /* max-height: 604px; */
      .banner-background {
        .container {
          width: 100%;
        }
      }
      .banner-content {
        margin: 28% 20px 0px;
        padding: 33px 0 28px;
        width: 100%;
        text-align: center;
        .banner-title {
          max-width: none;
        }
        .banner-desc {
          font-size: 16px;
          line-height: 22px;
          padding: 0 13%;
          max-width: 100% !important;
        }
        .banner-btn {
          margin: 0 auto;
        }
        .fine-print {
          font-size: 14px;
          &.dark {
            font-size: 14px;
          }
        }
        p.fine-print {
          padding-top: 16px;
        }
      }
      .bg-image {
        display: none;
      }
    }
    .app-download {
      flex-wrap: wrap;
      max-height: none;
      justify-content: center;
      .app-download-info {
        text-align: left;
        order: 2;
        flex-grow: unset;
        padding-left: 0px;
        .section-title {
          text-align: center;
        }
        ul {
          li {
            font-size: 18px;
          }
        }
        .appstore-images {
          justify-content: center;
          display: flex;
        }
      }
      .app-image {
        order: 1;
        max-width: 310px;
        margin-bottom: 31px;
        padding: 0px;
      }
    }
    .premium-shows-section {
      .premium-shows {
        .section-title {
          font-size: 14px;
        }
        .desc {
          font-size: 16px;
        }
      }
    }
  }
}

@media (max-width: 576px) {
  .home-page {
    .premium-perks {
      padding: 2% !important;
      .premium-perks-content-container {
        flex-wrap: wrap;
        .premium-perks-content {
          border-right: none !important;
          margin-top: 25px;
          width: 100% !important;
          box-sizing: border-box;
          h4 {
            font-size: 26px;
          }
          p {
            color: $black;
            font-size: 16px;
            font-family: Favorit, Arial, sans-serif;
          }
          .yellow-circle {
            width: 46px;
            height: 46px;
            display: flex;
            align-items: center;
            justify-content: center;
            span {
              font-size: 36px;
            }
          }
        }
      }
    }
    .app-download {
      .app-download-info {
      .title {
        font-size: 30px;
      }
      .appstore-images {
        img {
          margin: 0 0 !important;
        }
      }
      }
    }
    .premium-shows {
      padding: 28px 0px !important;
      margin-bottom: 0;
      .subtitle {
        font-size: 36px;
      }
      .desc {
        font-size: 18px;
      }
    }
    .app-download {
      .app-download-info {
        padding-left: 18px;
        .section-title {
          font-size: 14px;
          margin: 0 0 14px;
        }
        ul {
          font-size: 16px;
          padding-right: 5px;
          margin-bottom: 30px;
        }
        .appstore-images {
          margin-left: -18px;
        }
      }
    }
  }
}

@media (max-width: 384px) {
  .home-page {
    .banner {
      .banner-content {
        .banner-title {
          font-size: 35px;
          line-height: 40px;
          .next-line {
            white-space: unset;
            display: inline;
          }
        }
      }
    }
  }
}

</style>



// WEBPACK FOOTER //
// app/src/pages/Home.vue